'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, Heart, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPuzzleAuthorName } from '@/utils/puzzle-display';
import type { Puzzle } from '@/utils/util_types';
import { localizedPublicPath } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';

type PickupRowProps = {
    puzzles: Puzzle[];
    onSelect: (puzzle: Puzzle) => void;
    resolvingPuzzleId?: string | null;
    languageCode: string;
};

function formatCount(value?: number): string {
    const count = value ?? 0;
    if (count >= 10000) return `${(count / 10000).toFixed(1).replace(/\.0$/, '')}w`;
    if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(count);
}

function PickupPoster({
    puzzle,
    onSelect,
    disabled,
    href,
    languageCode,
}: {
    puzzle: Puzzle;
    onSelect: (puzzle: Puzzle) => void;
    disabled: boolean;
    href: string;
    languageCode: string;
}) {
    const langData = getLanguageDataByCode(languageCode);
    const [imageFailed, setImageFailed] = useState(false);
    const hasCover = Boolean(puzzle.cover_url) && !imageFailed;
    const authorName = getPuzzleAuthorName(puzzle, langData.puzzle_card_author_default);

    return (
        <Link
            href={href}
            onClick={(event) => {
                if (disabled) {
                    event.preventDefault();
                    return;
                }
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onSelect(puzzle);
            }}
            aria-disabled={disabled || undefined}
            aria-busy={disabled}
            aria-label={`${puzzle.title} · ${authorName}`}
            data-testid="pickup-card"
            className={cn(
                'group relative h-[224px] w-[168px] flex-none snap-start overflow-hidden border text-left',
                'border-lamp/20 border-t-lamp/55 bg-card sm:h-[280px] sm:w-[210px]',
                'transition-[border-color,box-shadow,transform] duration-300',
                'hover:-translate-y-1 hover:border-lamp/45 hover:shadow-[0_14px_34px_rgba(0,0,0,0.32)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                disabled && 'cursor-wait opacity-60',
            )}
        >
            {hasCover ? (
                <img
                    src={puzzle.cover_url ?? undefined}
                    alt=""
                    loading="lazy"
                    onError={() => setImageFailed(true)}
                    className="absolute inset-0 h-full w-full object-cover saturate-[0.82] transition-transform duration-500 group-hover:scale-[1.035]"
                />
            ) : (
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundColor: 'var(--card)',
                        backgroundImage: [
                            'radial-gradient(ellipse 180px 130px at 12% -8%, color-mix(in srgb, var(--lamp) 15%, transparent), transparent 68%)',
                            'radial-gradient(circle 1px at 1px 1px, color-mix(in srgb, var(--lamp) 7%, transparent), transparent 100%)',
                            'linear-gradient(155deg, color-mix(in srgb, var(--lamp) 7%, transparent), transparent 56%)',
                        ].join(','),
                        backgroundSize: 'auto, 8px 8px, auto',
                    }}
                />
            )}

            {hasCover && (
                <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.96)_0%,rgba(0,0,0,0.62)_46%,rgba(0,0,0,0.08)_78%)]" />
            )}

            <div className="absolute inset-x-0 bottom-0 flex flex-col p-3 sm:p-4">
                <h3 className={cn(
                    'line-clamp-2 font-[family-name:var(--font-playfair)] text-[16px] font-bold leading-[1.3] sm:text-[19px]',
                    hasCover ? 'text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.75)]' : 'text-card-foreground',
                )}>
                    {puzzle.title}
                </h3>

                {!hasCover && (
                    <p className="mt-2 line-clamp-5 text-[10px] leading-[1.6] text-muted-foreground sm:line-clamp-6 sm:text-[11px]">
                        {puzzle.soup}
                    </p>
                )}

                <div className="mt-3 h-px w-7 bg-lamp/45" />
                <div className={cn(
                    'mt-2.5 flex min-w-0 items-center gap-1.5 text-[11px]',
                    hasCover ? 'text-white/55' : 'text-muted-foreground',
                )}>
                    <User size={11} className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{authorName}</span>
                    <Heart size={11} className="ml-1 shrink-0" />
                    <span className="shrink-0 tabular-nums">{formatCount(puzzle.likedNum)}</span>
                </div>
            </div>
        </Link>
    );
}

export function PickupRow({ puzzles, onSelect, resolvingPuzzleId, languageCode }: PickupRowProps) {
    const langData = getLanguageDataByCode(languageCode);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        setCanScrollLeft(scroller.scrollLeft > 2);
        setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2);
    }, []);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const frame = requestAnimationFrame(updateScrollState);
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(scroller);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [puzzles.length, updateScrollState]);

    if (puzzles.length === 0) return null;

    function scroll(direction: -1 | 1) {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.scrollBy({
            left: direction * Math.min(scroller.clientWidth * 0.82, 720),
            behavior: 'smooth',
        });
    }

    return (
        <section className="mb-7 min-w-0 max-w-full overflow-hidden border-b border-lamp/10 py-5" aria-labelledby="pickup-heading" data-testid="pickup-row">
            <div className="mb-4 flex items-center gap-3">
                <h2 id="pickup-heading" className="font-[family-name:var(--font-playfair)] text-xs font-semibold italic uppercase tracking-normal text-lamp">
                    {langData.discover_pickup_title}
                </h2>
                <div className="h-px min-w-6 flex-1 bg-[linear-gradient(to_right,var(--lamp),transparent)] opacity-20" />
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => scroll(-1)}
                        disabled={!canScrollLeft}
                        aria-label="Scroll editor picks left"
                        title="Previous"
                        className="flex h-8 w-8 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-lamp/30 hover:text-foreground disabled:cursor-default disabled:opacity-25"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scroll(1)}
                        disabled={!canScrollRight}
                        aria-label="Scroll editor picks right"
                        title="Next"
                        className="flex h-8 w-8 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-lamp/30 hover:text-foreground disabled:cursor-default disabled:opacity-25"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            <div
                ref={scrollerRef}
                onScroll={updateScrollState}
                data-testid="pickup-scroller"
                className="flex max-w-full snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-4"
            >
                {puzzles.map((puzzle, index) => (
                    <PickupPoster
                        key={puzzle.id ?? index}
                        puzzle={puzzle}
                        onSelect={onSelect}
                        disabled={resolvingPuzzleId === puzzle.id}
                        href={localizedPublicPath(`/puzzle/${encodeURIComponent(puzzle.id ?? '')}`, languageCode)}
                        languageCode={languageCode}
                    />
                ))}
            </div>
        </section>
    );
}
